import { UseQueryResult } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  checkLoginUid,
  getCachedUid,
  getCurrentUid,
  setDisableAutoLogin,
} from "@/api/common";
import { ConfigPageAuthMode } from "@/hooks/useConfigPage/access";
import { defineShareInfoInMiniProgram } from "@/utils/share";
import SharePanelWithContext, {
  SharePanelWithContextRef,
} from "@/component/share-panel/SharePanelWithContext";
import { PageDataConfig } from "@/types/config-page";
import { getBrowserEnv } from "@/utils/tools";
import { appendParamsToUrl } from "@/utils/url";
import { getUnifiedLocation } from "@/utils/common";
import { isHostLoggedOut } from "@/utils/host-login-state";
import { redirectToHostLogin } from "@/utils/host-login-redirect";

const { isMyApp, isWXApp } = getBrowserEnv();

type ShareInfo = {
  shareMainTitle: string;
  shareSubTitle: string;
  shareImgUrl: string;
  sharePageUrl: string;
};

/**
 * 定位信息接口
 */
export type LocationInfo = {
  lng: number;
  lat: number;
  address?: string;
  isUseDefault?: boolean;
};

/**
 * 定位状态
 */
export type LocationStatus = "idle" | "loading" | "success" | "error";

type ConfigPageContextType = {
  pageCode?: string;
  versionCode?: string;
  navHeight?: number;
  configPageQuery?: UseQueryResult<PageDataConfig>;
  handleShareInfo?: (data: PageDataConfig) => Promise<void>;
  handleShare?: (data?: PageDataConfig) => void;
  shareInfoRef?: React.Ref<ShareInfo>;
  onNavShareClick?: () => void;
  setNavHeight?: (height: number) => void;
  sharePath?: string;
  setSharePath?: (path: string) => void;
  pageOrigin?: string;
  authMode?: ConfigPageAuthMode;
  loginRequired?: 0 | 1;
  isGuest?: boolean;
  openedAsGuest?: boolean;
  ensureLogin?: () => Promise<boolean>;
  refreshPage?: () => Promise<unknown>;
  /** 定位信息 */
  location?: LocationInfo | null;
  /** 定位状态 */
  locationStatus?: LocationStatus;
  /** 重新获取定位 */
  refetchLocation?: () => Promise<LocationInfo | null>;
};

const ConfigPageContext = createContext<ConfigPageContextType>({});

export const ConfigPageProvider = ({
  children,
  configPageQuery,
  authMode = "authenticated",
  onAuthenticated,
  sharePath = "",
  pageOrigin = "",
  handleShareUrl = (params: string) => params,
}: {
  children: React.ReactNode;
  sharePath?: string;
  pageOrigin?: string;
  authMode?: ConfigPageAuthMode;
  onAuthenticated?: () => void;
  handleShareUrl?: (params: string) => string;
} & Omit<
  ConfigPageContextType,
  | "pageCode"
  | "handleShareInfo"
  | "handleShare"
  | "shareInfoRef"
  | "onNavShareClick"
  | "setNavHeight"
  | "navHeight"
  | "setSharePath"
  | "authMode"
  | "loginRequired"
  | "isGuest"
  | "openedAsGuest"
  | "ensureLogin"
  | "refreshPage"
  | "location"
  | "locationStatus"
  | "refetchLocation"
>) => {
  const [navHeight, setNavHeight] = useState<number>(0);
  const [currentSharePath, setCurrentSharePath] = useState<string>(sharePath);
  const shareInfoRef = useRef<ShareInfo>({
    shareMainTitle: "",
    shareSubTitle: "",
    shareImgUrl: "",
    sharePageUrl: "",
  });

  // 定位相关状态
  const [location, setLocation] = useState<LocationInfo | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>("idle");
  const locationFetchingRef = useRef(false); // 是否正在获取定位

  const pageCode = configPageQuery?.data?.pageCode;
  const versionCode = configPageQuery?.data?.versionCode;
  const loginRequired = configPageQuery?.data?.loginRequired ?? 1;
  const isGuest = authMode === "guest";
  const openedAsGuestRef = useRef(isGuest);

  useEffect(() => {
    // 免登录访问期间，关闭联登 SDK 的"未登录自动跳转登录"兜底逻辑，
    // 避免埋点上报、组件曝光等静默取 uid 场景被强制拉去登录；
    // ensureLogin 内主动触发登录时会临时恢复
    setDisableAutoLogin(isGuest);
    return () => {
      setDisableAutoLogin(false);
    };
  }, [isGuest]);

  const ensureLogin = useCallback(async () => {
    // 宿主已声明未登录：本地缓存和接口探测都不可信，直接跳宿主登录页
    if (isHostLoggedOut()) {
      redirectToHostLogin(loginRequired === 1);
      return false;
    }

    // 仅探测登录态，不触发跳转登录：APP webview 首屏 sessionUid 尚未写入，
    // 用同步缓存判断会把已登录用户误判为未登录
    const loggedInUid = await checkLoginUid();
    if (loggedInUid) {
      onAuthenticated?.();
      return true;
    }

    // 主动触发登录场景：临时恢复联登 SDK 的自动跳转能力，
    // 避免与免登录访问期间关闭的 disableAutoLogin 标志冲突
    setDisableAutoLogin(false);
    const uid = await getCurrentUid();
    if (isGuest) {
      setDisableAutoLogin(true);
    }
    if (!uid) {
      return false;
    }
    onAuthenticated?.();
    return true;
  }, [onAuthenticated, isGuest, loginRequired]);

  const refreshPage = useCallback(async () => {
    return configPageQuery?.refetch();
  }, [configPageQuery]);

  const sharePanelRef = useRef<SharePanelWithContextRef>(null);

  // 需要定位的组件类型列表
  const LOCATION_REQUIRED_MODULES = ["dynamicStation", "memberBooking"];

  /**
   * 判断当前配置页是否包含需要定位的组件
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkNeedsLocation = (moduleList: any[] = []) => {
    return moduleList.some((module) =>
      LOCATION_REQUIRED_MODULES.includes(module.moduleType),
    );
  };

  /**
   * 获取定位 - 直接调用 getUnifiedLocation
   */
  const fetchLocation = useCallback(async (): Promise<LocationInfo | null> => {
    // 防止并发调用
    if (locationFetchingRef.current) {
      console.log("[useConfigPage] 定位进行中，跳过重复调用");
      return null;
    }

    locationFetchingRef.current = true;
    setLocationStatus("loading");

    try {
      const result = await getUnifiedLocation();
      const locationData: LocationInfo = {
        lng: result.lng,
        lat: result.lat,
        address: result.address,
        isUseDefault: result.isUseDefault,
      };
      setLocation(locationData);
      setLocationStatus("success");
      console.log("[useConfigPage] 定位成功:", locationData);
      return locationData;
    } catch (error) {
      console.error("[useConfigPage] 定位失败:", error);
      setLocationStatus("error");
      return null;
    } finally {
      locationFetchingRef.current = false;
    }
  }, []);

  /**
   * 重新获取定位（供子组件调用，如点击"重新定位"按钮）
   */
  const refetchLocation =
    useCallback(async (): Promise<LocationInfo | null> => {
      return fetchLocation();
    }, [fetchLocation]);

  // 配置数据加载完成后，根据是否包含需要定位的组件来决定是否调用定位
  useEffect(() => {
    const moduleList = configPageQuery?.data?.moduleList;
    if (moduleList && checkNeedsLocation(moduleList)) {
      console.log("[useConfigPage] 检测到需要定位的组件，开始获取定位");
      fetchLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configPageQuery?.data]);

  const handleShareInfo = useCallback(
    async (data: PageDataConfig) => {
      const {
        shareTitle,
        pageTitle,
        shareUrl,
        sharePageUrl,
        moduleList = [],
      } = data;

      const userUid = getCachedUid();
      const shareCurrentParams = {
        pageCode: data?.pageCode || "",
        ...(userUid ? { shareUid: userUid } : {}),
      };
      const shareCurrentUrl =
        currentSharePath ||
        `${window.location.origin}${window.location.pathname}#/configPage`;
      const defaultSharePageUrl = appendParamsToUrl(
        shareCurrentUrl,
        shareCurrentParams,
      );

      const currentSharePageUrl = handleShareUrl(
        sharePageUrl || defaultSharePageUrl,
      );

      const shareInfoObj = {
        shareMainTitle: shareTitle || pageTitle,
        shareSubTitle: shareTitle || pageTitle,
        shareImgUrl: shareUrl || "",
        sharePageUrl: currentSharePageUrl,
      };

      localStorage.setItem(
        `shareInfoObj${data?.pageCode}`,
        JSON.stringify(shareInfoObj),
      );
      localStorage.setItem(
        `pageVersionDto${data?.pageCode}`,
        JSON.stringify(data),
      );

      if (moduleList.length > 0) {
        localStorage.setItem(
          `pageModuleDto${data?.pageCode}`,
          JSON.stringify(moduleList[0]),
        );

        moduleList.forEach((ele) => {
          if (ele.moduleType == "busi-ranking") {
            localStorage.setItem(
              `pageModuleDto${ele.moduleId}`,
              JSON.stringify(ele),
            );
          }
        });
      }

      if (isMyApp || isWXApp) {
        defineShareInfoInMiniProgram({
          shareTitle: shareTitle || pageTitle,
          shareIcon: shareUrl || "",
          shareUrl: `/pages/webpage/index?pageUrl=${encodeURIComponent(currentSharePageUrl)}`,
        });
      }

      shareInfoRef.current = shareInfoObj;
    },
    [currentSharePath],
  );

  useEffect(() => {
    if (configPageQuery?.status === "success" && configPageQuery?.data) {
      handleShareInfo(configPageQuery.data);
    }
  }, [configPageQuery?.status, configPageQuery?.data, handleShareInfo]);

  const handleShare = (data?: PageDataConfig) => {
    if (configPageQuery?.data?.canShare === 0) {
      return;
    }
    if (data) {
      handleShareInfo(data);
      setTimeout(() => {
        sharePanelRef.current?.onOpen();
      }, 0);
    } else {
      sharePanelRef.current?.onClose();
    }
  };

  const onNavShareClick = () => {
    handleShare(configPageQuery?.data);
  };

  const handleSharePathChange = (path: string) => {
    setCurrentSharePath(path);
  };

  return (
    <ConfigPageContext.Provider
      value={{
        pageCode,
        versionCode,
        navHeight,
        configPageQuery,
        shareInfoRef,
        handleShareInfo,
        handleShare,
        onNavShareClick,
        setNavHeight,
        sharePath: currentSharePath,
        setSharePath: handleSharePathChange,
        pageOrigin,
        authMode,
        loginRequired,
        isGuest,
        openedAsGuest: openedAsGuestRef.current,
        ensureLogin,
        refreshPage,
        location,
        locationStatus,
        refetchLocation,
      }}
    >
      {children}
      <SharePanelWithContext ref={sharePanelRef} />
    </ConfigPageContext.Provider>
  );
};

export const useConfigPage = () => {
  const context = useContext(ConfigPageContext);
  if (!context) {
    throw new Error("useConfigPage must be used within ConfigPageProvider");
  }
  return context;
};

export const PageOriginEnums = {
  VIP_GOODS: "/apps/charge-web/charge-vip.html#/vip-goods",
  VIP: "/apps/charge-web/charge-vip.html#/",
};
